import { useCallback, useEffect } from 'react';
import { IFormState, IScheme, TElement } from '../types/calculator';
import { FieldErrors, FormState } from 'react-hook-form';
import { getEnrichedErrors, debouncedSubmitHandler, debouncedErrorHandler } from '../utils';
import { useDelayedSuccessHandler } from './useDelayedSuccessHandler';
import { usePreviousValuesEqualCurrent } from './usePreviousValuesEqualCurrent';

export interface IUseCalculatorCustomEventsDeps {
  formValues: IFormState;
  formState: FormState<IFormState>;
  eventTarget: HTMLFormElement | null;
  formScheme: TElement[];
}

export const useCalculatorCustomEvents = ({
  formValues,
  eventTarget,
  formState,
  formScheme,
}: IUseCalculatorCustomEventsDeps) => {
  const { isValid, errors } = formState;

  const submitHandler = debouncedSubmitHandler(eventTarget);
  const errorHandler = debouncedErrorHandler(eventTarget);

  const isPrevValuesEqualCurrent = usePreviousValuesEqualCurrent(formValues);
  const delayedSuccessHandler = useDelayedSuccessHandler(submitHandler);

  const handleError = useCallback(
    (currentErrors: FieldErrors<IFormState>) => {
      const enrichedErrors = getEnrichedErrors(currentErrors, formScheme as IScheme[]);
      errorHandler(enrichedErrors);
    },
    [errorHandler],
  );

  useEffect(() => {
    if (isValid && !isPrevValuesEqualCurrent) {
      delayedSuccessHandler(formValues, errors);
    } else {
      handleError(errors);
    }
  }, [isValid, formValues, errors]);
};
